import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import styled, { keyframes } from 'styled-components';
import { KEY_CODES } from '@utils';
import { usePrefersReducedMotion, useReveal } from '@hooks';

const StyledJobsSection = styled.section`
  max-width: 700px;

  .inner {
    display: flex;

    @media (max-width: 600px) {
      display: block;
    }

    // Prevent container from jumping
    @media (min-width: 700px) {
      min-height: 340px;
    }
  }
`;

const StyledTabList = styled.div`
  position: relative;
  z-index: 3;
  width: max-content;
  padding: 0;
  margin: 0;
  list-style: none;

  /* The strip is deliberately wider than the section so tabs can travel into
     the page gutter instead of stopping short of it. The mask below then hides
     whatever is out there: --fade-size matches the bleed exactly, so the faded
     band and the overhang are the same region. --fade-l / --fade-r are set by
     the scroll handler and are 0 at each end, which keeps the first and last
     tabs at full strength once you have actually reached them. */
  @media (max-width: 600px) {
    display: flex;
    overflow-x: auto;
    width: calc(100% + 100px);
    padding-left: 50px;
    /* Lets the last tab reach the section's right edge rather than stopping
       50px past it, where the mask would be fading. */
    padding-right: 50px;
    margin-left: -50px;
    margin-bottom: 30px;

    --fade-size: 50px;

    /* No horizontal bar: the tabs are the affordance, and a scrollbar under a
       two-pixel indicator reads as a second one. */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }

    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 calc(var(--fade-l, 0) * var(--fade-size)),
      #000 calc(100% - var(--fade-r, 0) * var(--fade-size)),
      transparent 100%
    );
    mask-image: linear-gradient(
      to right,
      transparent calc(var(--fade-l, 0) * var(--fade-size) * 0.5),
      #000 calc(var(--fade-l, 0) * var(--fade-size)),
      #000 calc(100% - var(--fade-r, 0) * var(--fade-size)),
      transparent calc(100% - var(--fade-r, 0) * var(--fade-size) * 0.5)
    );
  }
  @media (max-width: 480px) {
    width: calc(100% + 50px);
    padding-left: 25px;
    padding-right: 25px;
    margin-left: -25px;

    --fade-size: 25px;
  }

  li {
    &:first-of-type {
      @media (max-width: 600px) {
        margin-left: 50px;
      }
      @media (max-width: 480px) {
        margin-left: 25px;
      }
    }
    &:last-of-type {
      @media (max-width: 600px) {
        padding-right: 50px;
      }
      @media (max-width: 480px) {
        padding-right: 25px;
      }
    }
  }
`;

const StyledTabButton = styled.button`
  ${({ theme }) => theme.mixins.link};
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--tab-height);
  /* Fixed height + align-items:center centres the line box; the top-only
     inset drops the glyphs by half its value onto the true optical centre.
     The old 2px bottom padding was pushing them further up. */
  padding: calc(var(--optical) * 2) 20px 0;
  border-left: 2px solid var(--border);
  background-color: transparent;
  color: ${({ isActive }) => (isActive ? 'var(--text)' : 'var(--text-muted)')};
  font-family: var(--font-sans);
  font-size: var(--fz-lg);
  text-align: left;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: calc(var(--optical) * 2) 15px 0;
  }
  @media (max-width: 600px) {
    ${({ theme }) => theme.mixins.flexCenter};
    min-width: 120px;
    padding: 0 15px;
    border-left: 0;
    border-bottom: 2px solid var(--border);
    text-align: center;
  }

  &:hover,
  &:focus-visible {
    background-color: var(--surface);
  }
`;

const StyledHighlight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 2px;
  height: var(--tab-height);
  border-radius: var(--border-radius);
  background: var(--accent);
  transform: translateY(calc(${({ activeTabId }) => activeTabId} * var(--tab-height)));
  transition: transform 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
  transition-delay: 0.1s;

  @media (max-width: 600px) {
    top: auto;
    bottom: 0;
    width: 100%;
    max-width: var(--tab-width);
    height: 2px;
    margin-left: 50px;
    transform: translateX(calc(${({ activeTabId }) => activeTabId} * var(--tab-width)));
  }
  @media (max-width: 480px) {
    margin-left: 25px;
  }
`;

/* The container is given the active panel's measured height so the change
   between companies is animated rather than instant. Without it the section
   resizes in one frame and everything below, the research section included,
   jumps to a new position instead of moving there.

   height rather than max-height: a max-height large enough for the longest
   panel makes every shorter transition run at the wrong speed, since the
   property travels the full distance either way. */
/* The column holding the header and the panel stack, so the indent is set
   once rather than on each of them. */
const StyledTabColumn = styled.div`
  width: 100%;
  margin-left: 20px;

  @media (max-width: 600px) {
    margin-left: 0;
  }
`;

/* Position and dates for whichever company is selected.
   ------------------------------------------------------------------------
   Deliberately outside the panel stack. The panels crossfade, and a heading
   that fades out and back in while its text changes reads as a flicker for no
   information: the title and the dates are short, and swapping them outright
   is what a tab control is expected to do. Only the description, which is
   long enough that a hard cut would jar, crossfades. */
const StyledTabHeading = styled.div`
  padding: 10px 5px 0;

  h3 {
    margin-bottom: 2px;
    font-size: var(--fz-xxl);
    font-weight: 500;
    line-height: var(--lh-heading);

    .company {
      color: var(--text-emphasis);
    }
  }

  .range {
    margin: 0;
    color: var(--text-secondary);
    font-family: var(--font-sans);
    font-size: var(--fz-md);
  }
`;

const StyledTabPanels = styled.div`
  position: relative;
  width: 100%;
  height: ${({ height }) => (height ? `${height}px` : 'auto')};
  overflow: hidden;
  /* Matches --transition-slow's duration and the site's shared easing. A
     height change of a few hundred pixels at 0.2s reads as a jump; the ease
     is what keeps the section below it feeling carried rather than shoved. */
  transition: height 0.5s var(--easing);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* Each bullet rises a few pixels into place. Small on purpose: this is a list
   settling, not an entrance. */
const itemIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/* Every panel is stacked in the same place and only opacity moves, so
   switching company crossfades rather than removing one element and inserting
   another. The old version toggled the hidden attribute, which took the
   outgoing panel out of flow the instant the incoming one appeared: that is
   the flash. Nothing is hidden now, so there is nothing to flash.

   The container's height is measured and animated separately, since absolutely
   positioned panels contribute none of their own. */
const StyledTabPanel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  padding: var(--space-5) 5px 10px;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  visibility: ${({ isActive }) => (isActive ? 'visible' : 'hidden')};
  pointer-events: ${({ isActive }) => (isActive ? 'auto' : 'none')};
  transition: opacity 0.4s var(--easing), visibility 0.4s var(--easing);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  /* The bullets arrive one after another from the top rather than all at once
     with the panel. The panel itself only crossfades — it is never unmounted —
     so a CSS animation on the list would play once on first paint and never
     again; the inner content is keyed on whether this panel is the active one,
     which remounts it on a switch and restarts these.

     Delays are per nth-child because the markup comes from markdown and there
     is nowhere to set a custom property per item. Twelve is well past the
     longest entry; anything beyond it simply arrives with the twelfth. */
  ul {
    ${({ theme }) => theme.mixins.fancyList};

    li {
      animation: ${itemIn} 0.42s var(--easing) both;
    }

    ${Array.from(
      { length: 12 },
      (_, i) => `
      li:nth-child(${i + 1}) {
        animation-delay: ${(80 + i * 70) / 1000}s;
      }
    `,
    ).join('')}
  }

  @media (prefers-reduced-motion: reduce) {
    ul li {
      animation: none;
    }
  }
`;

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;

  const [activeTabId, setActiveTabId] = useState(0);
  // The heading is rendered once from whichever company is selected, rather
  // than once per panel, which is what lets it swap rather than crossfade.
  const activeJob = (jobsData[activeTabId] || jobsData[0]).node.frontmatter;
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);
  const panels = useRef([]);
  const [panelHeight, setPanelHeight] = useState(null);
  const tabListRef = useRef(null);
  const revealContainer = useReveal();
  const prefersReducedMotion = usePrefersReducedMotion();

  useLayoutEffect(() => {
    const measure = () => {
      const panel = panels.current[activeTabId];
      if (panel) {
        setPanelHeight(panel.scrollHeight);
      }
    };

    measure();

    // Reflowing the copy at a new width changes the panel's height, and the
    // stored one would otherwise clip it.
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeTabId]);

  const focusTab = () => {
    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus();
      return;
    }
    // If we're at the end, go to the start
    if (tabFocus >= tabs.current.length) {
      setTabFocus(0);
    }
    // If we're at the start, move to the end
    if (tabFocus < 0) {
      setTabFocus(tabs.current.length - 1);
    }
  };

  // Only re-run the effect if tabFocus changes
  useEffect(() => focusTab(), [tabFocus]);

  // Focus on tabs when using up & down arrow keys
  /* Which ends of the strip still have tabs beyond them. The mask reads these,
     so the fade only appears on a side you can actually scroll towards — the
     first and last tabs stay at full strength once you have reached them. */
  const updateFades = () => {
    const list = tabListRef.current;
    if (!list) {
      return;
    }
    const max = list.scrollWidth - list.clientWidth;
    list.style.setProperty('--fade-l', list.scrollLeft > 1 ? 1 : 0);
    list.style.setProperty('--fade-r', list.scrollLeft < max - 1 ? 1 : 0);
  };

  /* Bring a tab to the middle of the strip. Clamping to the scrollable range
     is what gives the behaviour at the ends for free: the first tab cannot
     centre without scrolling past 0, so it settles flush left instead, and the
     last one settles flush right. */
  const centreTab = index => {
    const list = tabListRef.current;
    const tab = tabs.current[index];
    if (!list || !tab) {
      return;
    }

    const max = list.scrollWidth - list.clientWidth;
    if (max <= 0) {
      // Wide viewport: the strip is a vertical column and does not scroll.
      return;
    }

    const centred = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
    list.scrollTo({
      left: Math.max(0, Math.min(centred, max)),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  const selectTab = index => {
    setActiveTabId(index);
    centreTab(index);
  };

  useEffect(() => {
    const list = tabListRef.current;
    if (!list) {
      return undefined;
    }

    let queued = false;
    const onScroll = () => {
      if (!queued) {
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          updateFades();
        });
      }
    };

    updateFades();
    list.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      list.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP: {
        e.preventDefault();
        setTabFocus(tabFocus - 1);
        break;
      }

      case KEY_CODES.ARROW_DOWN: {
        e.preventDefault();
        setTabFocus(tabFocus + 1);
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">Where I’ve Worked</h2>

      <div className="inner">
        <StyledTabList
          ref={tabListRef}
          role="tablist"
          aria-label="Job tabs"
          onKeyDown={e => onKeyDown(e)}>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { company } = node.frontmatter;
              return (
                <StyledTabButton
                  key={i}
                  isActive={activeTabId === i}
                  onClick={() => selectTab(i)}
                  ref={el => (tabs.current[i] = el)}
                  id={`tab-${i}`}
                  role="tab"
                  tabIndex={activeTabId === i ? '0' : '-1'}
                  aria-selected={activeTabId === i ? true : false}
                  aria-controls={`panel-${i}`}>
                  <span>{company}</span>
                </StyledTabButton>
              );
            })}
          <StyledHighlight activeTabId={activeTabId} />
        </StyledTabList>

        <StyledTabColumn>
          <StyledTabHeading>
            <h3>
              <span>{activeJob.title}</span>
              <span className="company">
                &nbsp;@&nbsp;
                {activeJob.url ? (
                  <a href={activeJob.url} className="inline-link">
                    {activeJob.company}
                  </a>
                ) : (
                  activeJob.company
                )}
              </span>
            </h3>
            <p className="range">{activeJob.range}</p>
          </StyledTabHeading>

          <StyledTabPanels height={panelHeight}>
            {jobsData &&
              jobsData.map(({ node }, i) => (
                <StyledTabPanel
                  key={i}
                  isActive={activeTabId === i}
                  ref={el => (panels.current[i] = el)}
                  id={`panel-${i}`}
                  role="tabpanel"
                  tabIndex={activeTabId === i ? '0' : '-1'}
                  aria-labelledby={`tab-${i}`}
                  aria-hidden={activeTabId !== i}>
                  {/* Keyed on active/inactive rather than on the index: the key
                      changing is what remounts this subtree, and remounting is
                      what makes the staggered entrance play again on a switch.
                      Transform and opacity do not affect layout, so the height
                      measured for the container is unaffected by it. */}
                  <div
                    key={activeTabId === i ? 'active' : 'idle'}
                    dangerouslySetInnerHTML={{ __html: node.html }}
                  />
                </StyledTabPanel>
              ))}
          </StyledTabPanels>
        </StyledTabColumn>
      </div>
    </StyledJobsSection>
  );
};

export default Jobs;
